import React, { useState, useCallback, useEffect } from 'react';

import { request } from 'utils/api';
import { Form, Button, Message } from 'semantic-ui-react';

import {
  connectorTypeOptions,
  connectorPowerTypeOptions,
  connectorFormatOptions,
} from 'utils/locations';
import { useUser } from 'contexts/user';
import { useFeatures } from 'contexts/features';
import { currentUserCanAccessProviderEndpoint } from 'utils/roles';
import { ConfigureConnector } from './ConfigureConnector';
import { validateConnectorIds } from 'utils/connector';

function determineDefaultsByConfiguration(configuration = [], connectorId) {
  const defaults = {};
  const config = {};
  configuration.forEach((item) => {
    config[item.key] = item.value;
  });
  const connectorType = config[`Connector${connectorId}-Type`];
  if (connectorType === 'Fixed cable') {
    defaults.standard = 'IEC_62196_T2';
    defaults.format = 'CABLE';
  }
  if (connectorType === 'Type 2 socket') {
    defaults.standard = 'IEC_62196_T2';
    defaults.format = 'SOCKET';
  }
  if (connectorType === 'Type 3 socket') {
    defaults.format = 'SOCKET';
  }
  if (connectorType === 'Type-1 fixed cable') {
    defaults.standard = 'IEC_62196_T1';
    defaults.format = 'CABLE';
  }
  if (connectorType === 'Type-2 fixed cable') {
    defaults.standard = 'IEC_62196_T2';
    defaults.format = 'CABLE';
  }
  if (connectorType === 'Type-3 fixed cable') {
    defaults.format = 'CABLE';
  }
  if (connectorType === 'Schuko socket') {
    defaults.standard = 'DOMESTIC_F';
    defaults.format = 'SOCKET';
  }
  const connectorMaxCurrent = config[`Connector${connectorId}-MaxCurrent`];
  if (connectorMaxCurrent) {
    defaults.maxAmperage = parseFloat(connectorMaxCurrent, 10);
  }
  if (defaults.standard === 'IEC_62196_T2' && !defaults.powerType) {
    defaults.powerType = 'AC_3_PHASE';
  }
  if (defaults.standard === 'IEC_62196_T2' && !defaults.maxVoltage) {
    defaults.maxVoltage = 230;
  }
  if (defaults.standard === 'IEC_62196_T2_COMBO' && !defaults.powerType) {
    defaults.powerType = 'DC';
  }
  if (defaults.standard === 'IEC_62196_T2_COMBO' && !defaults.maxVoltage) {
    defaults.maxVoltage = 250;
  }
  return defaults;
}

const rules = [
  {
    query: {
      powerType: 'AC_1_PHASE',
    },
    values: {
      maxVoltage: {
        max: 230,
        min: 0,
      },
      maxAmperage: {
        max: 32,
        min: 0,
      },
      standard: [
        'IEC_62196_T1',
        'IEC_62196_T2',
        'DOMESTIC_F',
        'IEC_62196_T3A',
        'IEC_62196_T3C',
      ],
    },
  },
  {
    query: {
      powerType: 'AC_1_PHASE',
      standard: 'IEC_62196_T1',
    },
    values: {
      format: ['CABLE'],
      maxVoltage: {
        max: 230,
        min: 100,
      },
    },
  },
  {
    query: {
      powerType: 'AC_1_PHASE',
      standard: 'IEC_62196_T2',
    },
    values: {
      format: ['CABLE', 'SOCKET'],
    },
  },
  {
    query: {
      powerType: 'AC_1_PHASE',
      standard: 'DOMESTIC_F',
    },
    values: {
      format: ['SOCKET'],
      maxVoltage: {
        max: 230,
        min: 0,
      },
      maxAmperage: {
        max: 32,
        min: 0,
      },
    },
  },
  {
    query: {
      powerType: 'AC_1_PHASE',
      standard: 'IEC_62196_T3A',
    },
    values: {
      format: ['CABLE', 'SOCKET'],
      maxVoltage: {
        max: 230,
        min: 100,
      },
      maxAmperage: {
        max: 64,
        min: 0,
      },
    },
  },
  {
    query: {
      powerType: 'AC_1_PHASE',
      standard: 'IEC_62196_T3C',
    },
    values: {
      format: ['CABLE', 'SOCKET'],
      maxVoltage: {
        max: 230,
        min: 100,
      },
      maxAmperage: {
        max: 64,
        min: 0,
      },
    },
  },
  {
    query: {
      powerType: 'AC_3_PHASE',
    },
    values: {
      standard: ['IEC_62196_T1', 'IEC_62196_T2', 'IEC_62196_T3C'],
    },
  },
  {
    query: {
      powerType: 'AC_3_PHASE',
      standard: 'IEC_62196_T1',
    },
    values: {
      format: ['CABLE'],
      maxVoltage: {
        max: 230,
        min: 100,
      },
      maxAmperage: {
        max: 64,
        min: 0,
      },
    },
  },
  {
    query: {
      powerType: 'AC_3_PHASE',
      standard: 'IEC_62196_T2',
    },
    values: {
      format: ['CABLE', 'SOCKET'],
      maxVoltage: {
        max: 230,
        min: 100,
      },
      maxAmperage: {
        max: 64,
        min: 0,
      },
    },
  },
  {
    query: {
      powerType: 'AC_3_PHASE',
      standard: 'IEC_62196_T3C',
    },
    values: {
      format: ['CABLE', 'SOCKET'],
      maxVoltage: {
        max: 230,
        min: 100,
      },
      maxAmperage: {
        max: 64,
        min: 0,
      },
    },
  },
  {
    query: {
      powerType: 'DC',
    },
    values: {
      standard: ['CHADEMO', 'IEC_62196_T2_COMBO'], //missing MCS which i dont know what is?
      format: ['CABLE'],
      maxVoltage: {
        min: 400,
        max: 900,
      },
      maxAmperage: {
        max: 440,
        min: 0,
      },
    },
  },
];

const connectorValidOptions = {
  standard: connectorTypeOptions,
  format: connectorFormatOptions,
  powerType: connectorPowerTypeOptions,
};

function applyRules(values) {
  let result = values;
  let validOptions = { ...connectorValidOptions };
  rules.forEach((rule) => {
    const match = Object.keys(rule.query).every((key) => {
      return result[key] === rule.query[key];
    });
    if (!match) return;

    const applied = { ...values };
    Object.keys(rule.values).forEach((key) => {
      // filter out any multiple choice, setting the field to undefined
      if (Array.isArray(rule.values[key])) {
        if (rule.values[key].length == 0) {
          applied[key] = rule.values[key][0];
        }
        if (!rule.values[key].includes(result[key])) {
          applied[key] = null;
        }
        validOptions[key] = validOptions[key].filter(({ value }) =>
          rule.values[key].includes(value)
        );
        // deal with min/max values
      } else if (rule.values[key].min || rule.values[key].max) {
        validOptions[key] = rule.values[key];
      } else {
        applied[key] = rule.values[key];
      }
    });
    result = { ...result, ...applied };
  });
  return { validOptions, values: result };
}

function getFormValues(evseController) {
  const connectors = (evseController.connectors || []).map((connector) => {
    return { ...connector };
  });

  connectors.forEach((connector) => {
    const detectedDefaults = determineDefaultsByConfiguration(
      evseController.configuration,
      connector.connectorId
    );
    Object.keys(detectedDefaults).forEach((key) => {
      if (!connector[key]) {
        connector[key] = detectedDefaults[key];
      }
    });
  });

  return {
    evseId: evseController.evseId,
    connectors,
  };
}

export function ConfigureConnectors(props) {
  const { hideEvseId, onSave, onLoading, formId, evseController } = props;
  const [formValues, setFormValues] = useState(getFormValues(evseController));

  const [validOptionsState, setValidOptionsState] = useState({});
  const [error, setError] = useState(null);
  const [overrideConnectorStatus, setOverrideConnectorStatus] = useState(false);
  const [autopickLoading, setAutopickLoading] = useState(false);
  const [autopickCompleted, setAutopickCompleted] = useState(false);

  const { provider } = useUser();
  const { hasFeature, isReady: isFeatureReady } = useFeatures();
  const customStationIdEnabled =
    (hasFeature('roaming_custom_station_id') && isFeatureReady) || false;
  const allowEditingEvseId =
    provider.enableCustomStationId &&
    provider.allowMisalignedConnectorEvseIds &&
    customStationIdEnabled;

  const onSubmit = useCallback(async () => {
    onLoading(true);
    setError(null);

    try {
      const connectorIds = formValues.connectors.map(
        (connector) => connector.connectorId
      );
      validateConnectorIds(connectorIds);

      await request({
        method: 'PATCH',
        path: `/1/evse-controllers/${evseController.id}/connectors`,
        body: {
          evseId: formValues.evseId,
          connectors: formValues.connectors.map((connector) => ({
            ...connector,
            connectorId: connector.connectorId.toString(),
          })),
        },
      });
      onSave();
    } catch (err) {
      setError(err);
    } finally {
      onLoading(false);
    }
  }, [formValues, evseController.id, onLoading, onSave]);

  const setField = useCallback((index, name, value) => {
    setFormValues((prev) => {
      const next = {
        ...prev,
        connectors: prev.connectors.map((c, i) =>
          i === index ? { ...c, [name]: value } : c
        ),
      };

      const { values, validOptions } = applyRules(next.connectors[index]);
      next.connectors[index] = values;

      // merge valid options instead of overwriting the whole object
      setValidOptionsState((vo) => ({ ...vo, [index]: validOptions }));

      return next;
    });
  }, []);

  // Sorry for using useEffect, but we need to apply the connector rules
  // when the form is mounted
  useEffect(() => {
    formValues.connectors.forEach((connector, index) => {
      setField(index, 'connectorId', connector.connectorId);
    });
  }, []);

  const autoPickEvseId = useCallback(async () => {
    onLoading(true);
    setAutopickLoading(true);

    try {
      const { data } = await request({
        method: 'POST',
        path: `/1/evse-ids/search`,
        body: { hideUsed: true, enableAutoPick: true },
      });

      onLoading(false);

      if (!data?.length) {
        setError(new Error('No auto pick evse IDs available'));
        setAutopickLoading(false);
        return;
      }

      setFormValues((prev) => ({ ...prev, evseId: data[0].identifier }));
      setAutopickCompleted(true);
      setAutopickLoading(false);
    } catch (err) {
      onLoading(false);
      setError(err);
      setAutopickLoading(false);
    }
  }, [onLoading]);

  const providerEvseControllersAccess = currentUserCanAccessProviderEndpoint(
    'evseControllers',
    'write'
  );

  return (
    <Form id={formId} error={Boolean(error)} onSubmit={onSubmit}>
      {error && <Message error content={error?.message} />}

      {!hideEvseId && (
        <Form.Input
          value={formValues.evseId}
          name="evseId"
          label="EVSE ID"
          autoComplete="disabled"
          readOnly
          type="text">
          <input style={{ backgroundColor: 'rgba(192,192,192,.25)' }} />
        </Form.Input>
      )}

      {autopickCompleted && (
        <Message
          info
          content="New EVSE ID allocated, please make sure to hit Save in order to make these changes permanent"
        />
      )}

      {formValues.evseId && providerEvseControllersAccess && (
        <div>
          <Button
            basic
            icon="arrow-rotate-left"
            content="Reset and autopick EVSE ID"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              autoPickEvseId();
            }}
            loading={autopickLoading}
          />
        </div>
      )}

      <Connectors
        formValues={formValues}
        evseController={evseController}
        overrideConnectorStatus={overrideConnectorStatus}
        setField={setField}
        hideEvseId={hideEvseId}
        validOptionsState={validOptionsState}
        allowEditingEvseId={allowEditingEvseId}
        setOverrideConnectorStatus={setOverrideConnectorStatus}
      />
    </Form>
  );
}

function Connectors({
  formValues,
  evseController,
  overrideConnectorStatus,
  setField,
  hideEvseId,
  validOptionsState,
  allowEditingEvseId,
  setOverrideConnectorStatus,
}) {
  const { connectors = [] } = formValues;

  if (!connectors.length) {
    return (
      <Message content="Please configure number of connectors first (or use Auto Configuration)" />
    );
  }

  let { connectorStatus } = evseController;

  if (!overrideConnectorStatus && !connectorStatus) {
    return (
      <Message>
        No connector status received yet, we need a status notification from the
        EVSE before we can configure connectors. Please note that this can
        happen when a duplicate EVSE controller exists with the same OCPP
        Identity. Make sure there is no other active connection of this OCPP
        identity for a different provider.
        <br />
        <br />
        <Button
          primary
          content="Ignore Connector Status"
          style={{ margin: 0 }}
          onClick={() => setOverrideConnectorStatus(true)}
        />
      </Message>
    );
  }

  let connectorStatusOverwritten = false;
  if (overrideConnectorStatus && !connectorStatus) {
    connectorStatus = {};
    formValues.connectors.forEach((c) => (connectorStatus[c.connectorId] = {}));
    connectorStatusOverwritten = true;
  }

  const statusBasedConnectorIds = Object.keys(connectorStatus).filter(
    (id) => id.toString() !== '0'
  );

  if (
    !connectorStatusOverwritten &&
    statusBasedConnectorIds.length < formValues.connectors.length
  ) {
    return (
      <Message content="No status has been received for all sockets yet. We need a status notification for all sockets from the EVSE before we can configure connectors. (Are the right number of connectors configured?)" />
    );
  }

  return (
    <div>
      {formValues.connectors.map((connector, index) => (
        <ConfigureConnector
          evseController={evseController}
          key={index}
          connector={connector}
          connectorStatus={connectorStatus}
          validOptions={validOptionsState[index] || connectorValidOptions}
          hideEvseId={hideEvseId}
          onFieldChange={(name, value) => setField(index, name, value)}
          allowEditingEvseId={allowEditingEvseId}
        />
      ))}
    </div>
  );
}
